import { useCallback, useContext, useMemo } from 'react'
import { AppContext, AppContextType } from '../AppContext'
import { UserContext, UserContextType } from '../UserContext'
import { LocalVideoTrack } from 'livekit-client'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { virtual_backgrounds, RuntimeConstants_BackgroundImage } from '../services/Constants'
import useAnalytics from '../services/useAnalytics'
import { AnalyticsEvents } from '../services/AnalyticsService'
import CrossPlatformDialog from './components/CrossPlatformDialog'
import DialogActionButtonsSection from './components/DialogActionButtonsSection'
import { DialogBody, DialogTitle } from '@/components/Dialog'
import { CameraPreview } from '@/components/CameraPreview'
import { Button } from '@/UIComponents2/Button'
import useVirtualBackgroundActions from '@/services/useVirtualBackgroundActions'

export default function ChooseVirtualBackgroundModal() {
    const { t } = useTranslation()
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state: userState, dispatch: userDispatch } = useContext(UserContext) as UserContextType
    const localTrack = useMemo(() => {
        if (userState.mediaStreams.video instanceof LocalVideoTrack) {
            return userState.mediaStreams.video
        }
        return null
    }, [userState.mediaStreams.video])

    const currentProcessor = useMemo(() => {
        return userState.mediaStreams.videoProcessor
    }, [userState.mediaStreams.videoProcessor])

    const showDialog = appState.appState.showChooseVirtualBackgroundModal

    const { logUserActivityWithContext } = useAnalytics()
    const { setVirtualBackground } = useVirtualBackgroundActions()

    const onClose = () => {
        appDispatch({
            type: 'update_app_state',
            value: { showChooseVirtualBackgroundModal: false },
        })
    }

    const onChooseVirtualBackground = useCallback(
        async (background: RuntimeConstants_BackgroundImage | 'blur' | null) => {
            setVirtualBackground(
                background && typeof background === 'object' ? background.name : background,
                localTrack,
                currentProcessor
            )

            if (!background) {
                logUserActivityWithContext(AnalyticsEvents.VIRTUAL_BACKGROUND_DISABLED)
            } else {
                logUserActivityWithContext(AnalyticsEvents.VIRTUAL_BACKGROUND_PREVIEW_CHANGED, {
                    backgroundName: background === 'blur' ? 'blur' : background.name,
                })
            }
        },
        [localTrack, userDispatch, currentProcessor]
    )

    if (!showDialog) {
        return null
    }

    return (
        <CrossPlatformDialog
            frameName={`Dialog:chooseVirtualBackground`}
            onDefaultClose={onClose}
            classNameBrowser="max-h-[90vh]"
        >
            <div className="flex flex-col justify-between">
                <DialogBody>
                    <div className="flex flex-col h-full">
                        <div className="flex gap-2">
                            <DialogTitle>{t('Select virtual background')}</DialogTitle>
                            <div className="text-[10px]! align-super text-muse-yellow!">{t('BETA')}</div>
                        </div>

                        <CameraPreview
                            className="h-[260px] w-auto"
                            videoTrack={userState.mediaStreams.video}
                            isPermissionsDenied={false}
                        />

                        <div className="pt-[20px] pb-[5px]">{t('Backgrounds')}</div>
                        <div className="max-h-[245px] overflow-y-scroll p-[2px] pb-8">
                            <div className="grid grid-cols-4 gap-4">
                                <Button
                                    className={clsx(
                                        'aspect-square overflow-hidden rounded-md text-white text-xs bg-[#3D3D3D] flex items-center justify-center transition-all duration-150 relative',
                                        'sm:hover:opacity-80',
                                        {
                                            'ring-2 ring-muse-yellow':
                                                userState.mediaSelection.selectedVirtualBackground === null,
                                        }
                                    )}
                                    onClick={() => onChooseVirtualBackground(null)}
                                >
                                    {t('None')}
                                </Button>
                                <Button
                                    className={clsx(
                                        'aspect-square overflow-hidden rounded-md text-white text-xs flex items-center justify-center transition-all duration-150 relative',
                                        'sm:hover:opacity-80',
                                        {
                                            'ring-2 ring-muse-yellow':
                                                userState.mediaSelection.selectedVirtualBackground === 'blur',
                                        }
                                    )}
                                    onClick={() => onChooseVirtualBackground('blur')}
                                >
                                    <div className="absolute overflow-hidden inset-0 blur-xs bg-[#202122] bg-[url('https://downloads.musesessions.co/backgrounds/jail-1.jpg_thumb.webp')] bg-cover bg-center"></div>
                                    <span className="relative z-10">{t('Blur')}</span>
                                </Button>

                                {virtual_backgrounds?.map((background) => (
                                    <Button
                                        key={background.id}
                                        className={clsx(
                                            'aspect-square rounded-md overflow-hidden bg-[#202122] transition-all duration-150',
                                            'sm:hover:opacity-80',
                                            {
                                                'ring-2 ring-muse-yellow':
                                                    userState.mediaSelection.selectedVirtualBackground ===
                                                    background.name,
                                            }
                                        )}
                                        onClick={() => onChooseVirtualBackground(background)}
                                    >
                                        <img
                                            src={background.thumb}
                                            alt={background.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                </DialogBody>

                <DialogActionButtonsSection
                    rightButtonText={t('Done')}
                    onRightButtonClicked={() => {
                        logUserActivityWithContext(AnalyticsEvents.VIRTUAL_BACKGROUND_SELECTED, {
                            backgroundName: currentProcessor?.name === 'blur' ? 'blur' : currentProcessor?.name,
                        })
                        onClose()
                    }}
                />
            </div>
        </CrossPlatformDialog>
    )
}
